import { GlDisclosureDropdown, GlDisclosureDropdownGroup, GlDisclosureDropdownItem, GlTooltipDirective } from '/builds/gitlab/gitlab-ee/node_modules/@gitlab/ui/src/index.js?vue3';
import { s__, __ } from '~/locale';
import { visitUrl, appendLineRangeHashToUrl } from '~/lib/utils/url_utility';
import Tracking from '/builds/gitlab/gitlab-ee/app/assets/javascripts/tracking/index.js?vue3';
import ConfirmForkModal from '/builds/gitlab/gitlab-ee/app/assets/javascripts/vue_shared/components/web_ide/confirm_fork_modal.vue?vue3';
import { keysFor, GO_TO_PROJECT_WEBIDE } from '~/behaviors/shortcuts/keybindings';
import { keyboardShortcutsDisabled } from '~/behaviors/shortcuts/shortcuts_disabled';
import { KEY_EDIT, KEY_WEB_IDE, KEY_GITPOD, KEY_PIPELINE_EDITOR } from './constants';
export const i18n = {
    webIdeText: s__('WebIDE|Quickly and easily edit multiple files in your project.'),
    webIdeTooltip: s__('WebIDE|Quickly and easily edit multiple files in your project. Press . to open'),
    toggleText: __('Edit'),
    gitpodText: __('Launch a ready-to-code development environment for your project.')
};
const TRACKING_ACTION_NAME = 'click_consolidated_edit';
export default {
    name: 'CEWebIdeLink',
    components: {
        GlDisclosureDropdown,
        GlDisclosureDropdownGroup,
        GlDisclosureDropdownItem,
        ConfirmForkModal
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    i18n,
    mixins: [
        Tracking.mixin()
    ],
    props: {
        isFork: {
            type: Boolean,
            required: false,
            default: false
        },
        needsToFork: {
            type: Boolean,
            required: false,
            default: false
        },
        needsToForkWithWebIde: {
            type: Boolean,
            required: false,
            default: false
        },
        isGitpodEnabledForUser: {
            type: Boolean,
            required: false,
            default: false
        },
        isBlob: {
            type: Boolean,
            required: false,
            default: false
        },
        showEditButton: {
            type: Boolean,
            required: false,
            default: true
        },
        showWebIdeButton: {
            type: Boolean,
            required: false,
            default: true
        },
        isGitpodEnabledForInstance: {
            type: Boolean,
            required: false,
            default: false
        },
        showPipelineEditorButton: {
            type: Boolean,
            required: false,
            default: false
        },
        editUrl: {
            type: String,
            required: false,
            default: ''
        },
        buttonVariant: {
            type: String,
            required: false,
            default: null
        },
        pipelineEditorUrl: {
            type: String,
            required: false,
            default: ''
        },
        webIdeUrl: {
            type: String,
            required: false,
            default: ''
        },
        webIdeText: {
            type: String,
            required: false,
            default: ''
        },
        gitpodUrl: {
            type: String,
            required: false,
            default: ''
        },
        gitpodText: {
            type: String,
            required: false,
            default: ''
        },
        disableForkModal: {
            type: Boolean,
            required: false,
            default: false
        },
        forkPath: {
            type: String,
            required: false,
            default: ''
        },
        forkModalId: {
            type: String,
            required: false,
            default: ''
        },
        cssClasses: {
            type: String,
            required: false,
            default: '@sm/panel:gl-ml-3'
        },
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        customTooltipText: {
            type: String,
            required: false,
            default: __('You cannot edit this file')
        }
    },
    emits: [
        'edit',
        'hidden',
        'shown'
    ],
    data () {
        return {
            showForkModal: false
        };
    },
    computed: {
        actions () {
            if (!this.isBlob) {
                return [
                    this.pipelineEditorAction,
                    this.editAction
                ].filter(Boolean);
            }
            return [
                this.pipelineEditorAction,
                this.webIdeAction,
                this.editAction,
                this.gitpodAction
            ].filter(Boolean);
        },
        hasActions () {
            return this.actions.length > 0;
        },
        editButtonVariant () {
            if (this.buttonVariant) {
                return this.buttonVariant;
            }
            return this.isBlob ? 'confirm' : 'default';
        },
        editAction () {
            if (!this.showEditButton) return null;
            const handleOptions = this.needsToFork ? {
                href: '#modal-confirm-fork-edit',
                handle: ()=>{
                    if (this.disableForkModal) {
                        this.$emit('edit', 'simple');
                        return;
                    }
                    this.showModal('showForkModal');
                }
            } : {
                href: this.editUrl
            };
            return {
                key: KEY_EDIT,
                text: __('Edit single file'),
                secondaryText: __('Edit this file only.'),
                tracking: {
                    action: TRACKING_ACTION_NAME,
                    label: 'single_file'
                },
                ...handleOptions
            };
        },
        shortcutsDisabled () {
            return keyboardShortcutsDisabled();
        },
        webIdeActionShortcutKey () {
            return keysFor(GO_TO_PROJECT_WEBIDE)[0];
        },
        webIdeActionText () {
            if (this.webIdeText) {
                return this.webIdeText;
            }
            if (this.isFork) {
                return __('Edit fork in Web IDE');
            }
            return __('Open in Web IDE');
        },
        webIdeAction () {
            if (!this.showWebIdeButton) return null;
            const handleOptions = this.needsToForkWithWebIde ? {
                handle: ()=>{
                    if (this.disableForkModal) {
                        this.$emit('edit', 'ide');
                        return;
                    }
                    this.showModal('showForkModal');
                }
            } : {
                handle: ()=>{
                    const url = appendLineRangeHashToUrl(this.webIdeUrl);
                    visitUrl(url, true);
                }
            };
            return {
                key: KEY_WEB_IDE,
                text: this.webIdeActionText,
                secondaryText: this.$options.i18n.webIdeText,
                shortcut: this.webIdeActionShortcutKey,
                tracking: {
                    action: TRACKING_ACTION_NAME,
                    label: 'web_ide'
                },
                ...handleOptions
            };
        },
        gitpodActionText () {
            return this.gitpodText || __('Open in Ona');
        },
        computedShowGitpodButton () {
            return this.isGitpodEnabledForInstance && this.isGitpodEnabledForUser && this.gitpodUrl;
        },
        pipelineEditorAction () {
            if (!this.showPipelineEditorButton) {
                return null;
            }
            const secondaryText = __('Edit, lint, and visualize your pipeline.');
            return {
                key: KEY_PIPELINE_EDITOR,
                text: __('Edit in pipeline editor'),
                secondaryText,
                href: this.pipelineEditorUrl,
                tracking: {
                    action: TRACKING_ACTION_NAME,
                    label: 'pipeline_editor'
                }
            };
        },
        gitpodAction () {
            if (!this.computedShowGitpodButton) return null;
            const handleOptions = {
                handle: ()=>{
                    visitUrl(this.gitpodUrl, true);
                }
            };
            return {
                key: KEY_GITPOD,
                text: this.gitpodActionText,
                secondaryText: this.$options.i18n.gitpodText,
                tracking: {
                    action: TRACKING_ACTION_NAME,
                    label: 'gitpod'
                },
                ...handleOptions
            };
        },
        mountForkModal () {
            const { disableForkModal, showWebIdeButton, showEditButton } = this;
            if (disableForkModal) return false;
            return showWebIdeButton || showEditButton;
        },
        tooltipText () {
            return this.disabled ? this.customTooltipText : '';
        }
    },
    methods: {
        showModal (dataKey) {
            this[dataKey] = true;
        },
        executeAction (action) {
            this.track(action.tracking.action, {
                label: action.tracking.label
            });
            action.handle?.();
        }
    }
};
